import React, {
  type ComponentType,
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { Global404, Route404 } from '@saviynt/common';
import {
  AlertBannerProvider,
  Loader,
  OverlayProvider,
  StackingAlertBanner,
} from '@saviynt/design-system';
import cx from 'classnames';

import { useGetIdentityListMutation } from '../../features/ServiceIdentity';
import {
  ALERT_BANNER_DEFAULT,
  BASENAME,
  SAVI_ASSISTANT_ALLOWED_URLS,
  SAVI_ASSISTANT_BASENAME,
  SAVI_ASSISTANT_CHAT,
  SERVICE_IDENTITY_ALLOWED_URLS,
  SERVICE_IDENTITY_LIST,
  SERVICE_IDENTITY_LIST_CREATE,
  SYSTEM_DATA_THEMES,
} from '../../shared/constants';
import { useTypedSelector } from '../../shared/hooks';
import { globalActions } from '../../shared/store/globalSlice';
import {
  hideHostAppElements,
  hideHostAppElementsCleanup,
  removeDynamicBodyClasses,
} from '../../shared/utils';

import './index.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyModule = Promise<{ default: ComponentType<any> }>;

const Chat = lazy(
  () => import('../../pages/Chat/index.js') as unknown as LazyModule
);
const CreateServiceIdentity = lazy(
  () =>
    import(
      '../../pages/CreateServiceIdentity/index.js'
    ) as unknown as LazyModule
);
const Request = lazy(
  () => import('../../pages/Request/index.js') as unknown as LazyModule
);
const ServiceIdentity = lazy(
  () => import('../../pages/ServiceIdentity/index.js') as unknown as LazyModule
);

/**
 * Isolated alert banner that subscribes to its own Redux state, preventing
 * alert banner updates from re-rendering the parent route tree.
 */
const GlobalAlertBanner = memo(function GlobalAlertBanner() {
  const dispatch = useDispatch();
  const alertBanner = useTypedSelector(
    (state) => state.savicopilot.global.alertBanner
  );

  const handleCancel = useCallback(() => {
    dispatch(
      globalActions.setGlobalAlertBanner({
        ...ALERT_BANNER_DEFAULT,
        isOpen: false,
      })
    );
  }, [dispatch]);

  return (
    <StackingAlertBanner
      colorTheme={alertBanner.kind}
      title={alertBanner.title}
      description={alertBanner.description}
      shouldAutoDismiss
      isVisible={alertBanner.isOpen}
      onCancel={handleCancel}
    />
  );
});

interface AllowedRoutes {
  staticRoutes: string[];
  dynamicRoutes: string[];
  blockedUrlList: string[];
  blockedNames: string[];
}

/**
 * Top-level route configuration for Savi Copilot. Sets up the browser router
 * with routes for Savi Assistant, Chat, and Service Identity pages, along with
 * a global stacking alert banner and 404 handling.
 *
 * @returns The routed application with overlay and alert banner providers
 */
function Routes() {
  const location = useLocation();

  const {
    user: { isMenuPinned, isSaviEnabled },
  } = useTypedSelector((state) => state);

  const [
    fetchIdentityList,
    {
      isLoading: isServiceListLoading,
      isUninitialized: isServiceListUninitialized,
      isError: isServiceListError,
      error: serviceListError,
    },
  ] = useGetIdentityListMutation();

  const allowedRoutes: AllowedRoutes = {
    staticRoutes: SERVICE_IDENTITY_ALLOWED_URLS,
    dynamicRoutes: [],
    blockedUrlList: [],
    blockedNames: [],
  };

  if (isSaviEnabled) {
    allowedRoutes.staticRoutes = [
      ...allowedRoutes.staticRoutes,
      ...SAVI_ASSISTANT_ALLOWED_URLS,
    ];
  }

  const isServiceIdentityRoute = useMemo(
    () =>
      location.pathname === SERVICE_IDENTITY_LIST ||
      location.pathname === SERVICE_IDENTITY_LIST_CREATE,
    [location.pathname]
  );

  useEffect(() => {
    (hideHostAppElements as () => void)();
    (removeDynamicBodyClasses as () => void)();

    return () => (hideHostAppElementsCleanup as () => void)();
  }, []);

  useEffect(() => {
    if (isServiceIdentityRoute) {
      fetchIdentityList({
        search: '',
        filter: {},
        offset: 0,
        max: 1,
        sort: 'name',
        order: 'asc',
      }).catch(() => {});
    }
  }, [fetchIdentityList, isServiceIdentityRoute]);

  useEffect(() => {
    if (isServiceIdentityRoute && isServiceListError) {
      const { status } = serviceListError as { status: number };

      if (status === 403) {
        window.location.href = '/ECM/login/deniednew';
      }
    }
  }, [isServiceListError, serviceListError, isServiceIdentityRoute]);

  const alertBannerClasses = useMemo(
    () =>
      cx(
        'Savi-Copilot-alertBanner',
        location.pathname === SAVI_ASSISTANT_BASENAME &&
          'Savi-Copilot-alertBanner--isHeader',
        isServiceIdentityRoute && 'Savi-Copilot-alertBanner--isHeader',
        isMenuPinned && 'Savi-Copilot-alertBanner--isMenuPinned'
      ),
    [location.pathname, isMenuPinned, isServiceIdentityRoute]
  );

  if (
    isServiceIdentityRoute &&
    (isServiceListUninitialized || isServiceListLoading || isServiceListError)
  ) {
    return (
      <div className='ServiceIdentity-loaderWrapper'>
        <Loader kind='loop' format='main' color='brand' />
      </div>
    );
  }

  return (
    <Suspense
      key='SaviCopilot'
      fallback={
        <div>
          <FormattedMessage
            id='KmuT+3'
            defaultMessage='Loading...'
            description='Loading'
          />
        </div>
      }>
      <Router basename={BASENAME}>
        {/* @ts-expect-error - TODO: Sagar */}
        <Route404 routes={allowedRoutes} render={() => <Global404 />}>
          <Switch>
            <OverlayProvider>
              <AlertBannerProvider
                dataTheme={SYSTEM_DATA_THEMES.LIGHT}
                className={alertBannerClasses}>
                <Route
                  exact
                  path={SAVI_ASSISTANT_BASENAME}
                  component={Request}
                />
                <Route exact path={SAVI_ASSISTANT_CHAT} component={Chat} />
                <Route
                  exact
                  path={SERVICE_IDENTITY_LIST}
                  component={ServiceIdentity}
                />
                <Route
                  exact
                  path={SERVICE_IDENTITY_LIST_CREATE}
                  component={CreateServiceIdentity}
                />
                <GlobalAlertBanner />
              </AlertBannerProvider>
            </OverlayProvider>
          </Switch>
        </Route404>
      </Router>
    </Suspense>
  );
}

export default Routes;
